/**
 * Shared reward calculation module.
 *
 * Single source of truth for computing weekly pool rewards, per-tile gross,
 * per-tile net (after Harberger tax), cross-pool aggregation, and display
 * formatting. Handles three reward-token classes:
 *
 *   1. ETH/WETH   -> convert to USD via ethPrice oracle
 *   2. USDC/USDT  -> 1:1 with USD
 *   3. Exotic     -> display in native token units (no USD conversion)
 *
 * When a USD price becomes available for an exotic token (via tokenPrices),
 * it automatically upgrades to class 1/2 behaviour with zero code changes.
 */

import { formatUSD, formatTokenCompact } from '@/lib/utils';
import { weeklyTax } from '@/lib/tax';

// ---------------------------------------------------------------------------
// Token classification
// ---------------------------------------------------------------------------

export type RewardKind = 'usd' | 'native';

const WETH     = '0x4200000000000000000000000000000000000006';
const ETH_ZERO = '0x0000000000000000000000000000000000000000';
const USDC     = '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913';
const USDC_BRG = '0xd9aaec86b65d86f6a7b5b1b0c42ffa531710b6b1';
const USDT     = '0xfde4c96c8593536e31f229ea8f37b2ada2699bb2';

const ETH_ADDRS    = new Set([WETH, ETH_ZERO]);
const STABLE_ADDRS = new Set([USDC, USDC_BRG, USDT]);

export interface RewardTokenInfo {
  kind: RewardKind;
  /** USD price per whole token. >0 for 'usd' kind, 0 for 'native'. */
  priceUsd: number;
  decimals: number;
  symbol?: string;
}

/**
 * Classify a yield-token address. Mirrors the indexer's
 * `computeRewardAmountUsd` logic so client and server agree on which
 * tokens are USD-convertible.
 *
 * When `tokenPrices` contains a non-zero price for an otherwise-exotic
 * address, the token is automatically promoted to 'usd' kind. This is the
 * upgrade path for adding USD conversion to any token in the future.
 */
export function resolveRewardTokenInfo(
  yieldTokenAddress: string | undefined,
  decimals: number,
  ethPrice: number,
  tokenPrices?: Record<string, number>,
  symbol?: string,
): RewardTokenInfo {
  const addr = (yieldTokenAddress || ETH_ZERO).toLowerCase();

  if (ETH_ADDRS.has(addr))
    return { kind: 'usd', priceUsd: ethPrice, decimals: 18, symbol: 'ETH' };

  if (STABLE_ADDRS.has(addr))
    return { kind: 'usd', priceUsd: 1, decimals: decimals || 6, symbol: symbol || 'USDC' };

  const externalPrice = tokenPrices?.[addr] ?? 0;
  if (externalPrice > 0)
    return { kind: 'usd', priceUsd: externalPrice, decimals, symbol };

  return { kind: 'native', priceUsd: 0, decimals, symbol };
}

// ---------------------------------------------------------------------------
// Core calculations
// ---------------------------------------------------------------------------

const ONE_WEEK_S = 7 * 24 * 60 * 60;
const USDC_SCALE = 1_000_000; // 6 decimals

export interface PoolRewardParams {
  totalRewardsRaw: bigint;
  rewards7dRaw?: bigint;
  poolCreatedAt: number;
  rewardToken: RewardTokenInfo;
  tileCount?: number;
}

export interface RewardResult {
  /** USDC 6-dec for 'usd' kind, raw token units for 'native' kind. */
  amount: bigint;
  kind: RewardKind;
  is7dBased: boolean;
  symbol?: string;
  decimals: number;
}

/**
 * Convert a raw token amount to USDC 6-decimal bigint using the token's
 * price and decimals.
 */
function rawToUsdc6(raw: bigint, decimals: number, priceUsd: number): bigint {
  const human = Number(raw) / 10 ** decimals;
  return BigInt(Math.floor(human * priceUsd * USDC_SCALE));
}

/**
 * Total weekly rewards for a pool/grid.
 *
 * For 'usd' kind: returns USDC 6-decimal bigint.
 * For 'native' kind: returns raw token units extrapolated to one week.
 *
 * Priority:
 *   1. Pools older than 7 days: trailing 7d data is canonical (even when 0).
 *      A pool that hasn't accrued anything in the last week is genuinely
 *      paying 0/week now — surfacing the historical all-time average would
 *      mislead holders into thinking their tile still earns.
 *   2. Pools younger than 7 days with 7d-so-far data: extrapolate to a full week.
 *   3. Young pools with no 7d data: fall back to the all-time average.
 */
export function weeklyPoolRewards(params: PoolRewardParams): RewardResult {
  const { totalRewardsRaw, rewards7dRaw = 0n, poolCreatedAt, rewardToken } = params;
  const { kind, priceUsd, decimals, symbol } = rewardToken;
  const base: Omit<RewardResult, 'amount' | 'is7dBased'> = { kind, symbol, decimals };

  const now = Math.floor(Date.now() / 1000);
  const ageSeconds = Math.max(1, now - poolCreatedAt);

  if (!poolCreatedAt) return { ...base, amount: 0n, is7dBased: false };

  if (kind === 'usd') {
    if (ageSeconds > ONE_WEEK_S) {
      return { ...base, amount: rawToUsdc6(rewards7dRaw, decimals, priceUsd), is7dBased: true };
    }
    if (rewards7dRaw > 0n) {
      const extrapolated = rawToUsdc6(rewards7dRaw, decimals, priceUsd);
      const scaled = (extrapolated * BigInt(ONE_WEEK_S)) / BigInt(ageSeconds);
      return { ...base, amount: scaled, is7dBased: false };
    }
    if (totalRewardsRaw === 0n) return { ...base, amount: 0n, is7dBased: false };
    const totalUsdc = rawToUsdc6(totalRewardsRaw, decimals, priceUsd);
    const weekly = (totalUsdc * BigInt(ONE_WEEK_S)) / BigInt(ageSeconds);
    return { ...base, amount: weekly, is7dBased: false };
  }

  // 'native' kind: same extrapolation logic but keep raw token units.
  // We work in raw units throughout. The "weekly" value is the raw amount
  // the pool distributes per week in native token units.
  if (ageSeconds > ONE_WEEK_S) {
    return { ...base, amount: rewards7dRaw, is7dBased: true };
  }
  if (rewards7dRaw > 0n) {
    const scaled = (rewards7dRaw * BigInt(ONE_WEEK_S)) / BigInt(ageSeconds);
    return { ...base, amount: scaled, is7dBased: false };
  }
  if (totalRewardsRaw === 0n) return { ...base, amount: 0n, is7dBased: false };
  const weekly = (totalRewardsRaw * BigInt(ONE_WEEK_S)) / BigInt(ageSeconds);
  return { ...base, amount: weekly, is7dBased: false };
}

/**
 * Gross rewards per tile = weeklyPoolRewards / tileCount.
 */
export function grossRewardsPerTile(params: PoolRewardParams): RewardResult {
  const tiles = BigInt(params.tileCount ?? 100);
  const pool = weeklyPoolRewards(params);
  const perTile = pool.amount / tiles;
  return { ...pool, amount: perTile };
}

export interface TaxTokenOpts {
  /** Decimals of the tax/deposit token (defaults to 6 = USDC). */
  taxTokenDecimals?: number;
  /** USD price of one whole tax token (defaults to 1 = stablecoin). */
  taxTokenPriceUsd?: number;
}

/**
 * Sanity bound on per-tile weekly net (USDC-6dp). $10M/week per tile is
 * already comfortably outside any plausible Harberger pool — anything
 * past that is the signature of an input-coherence failure (e.g. a
 * `decimals=18` × `price=1` race in `useLobbySeats`, stale seats
 * surviving a cross-grid switch, a future caller that forgets to thread
 * the resolved tax-token price). Mirrors the same threshold used by
 * `takeoverToUsdSafe` in `lib/drawer-utils.ts`.
 */
export const MAX_SANE_NET_USDC6 = 10_000_000n * 1_000_000n;

/**
 * Net rewards per tile after Harberger tax. Returns `null` when the
 * computed net's magnitude exceeds `MAX_SANE_NET_USDC6` — that's the
 * canonical signal that one of the inputs (most often `taxTokenDecimals`
 * vs. `taxTokenPriceUsd`) was inconsistent with the seat's units, and
 * the result would be an explosive nonsense like the −$Bn renders the
 * lobby tooltip used to flash mid-board-switch. Callers naturally hide
 * the row instead of pinning each surface to its own input-shape guard.
 *
 * Only meaningful for 'usd' kind (tax is in USDC). For 'native' kind,
 * returns the gross unchanged since tax and rewards are in different
 * units.
 *
 * When the tax token differs from the reward token (e.g. TAKEOVER
 * 18-dec tax on a grid that distributes USDC rewards), pass `opts` so
 * the weekly tax is converted to USDC-6 before subtracting from gross.
 */
export function netRewardsPerTile(
  gross: RewardResult,
  tilePrice: bigint,
  taxRateBps: bigint,
  opts?: TaxTokenOpts,
): RewardResult | null {
  if (gross.kind !== 'usd') return gross;
  const taxRaw = weeklyTax(tilePrice, taxRateBps);
  const ttDec = opts?.taxTokenDecimals ?? 6;
  const ttPrice = opts?.taxTokenPriceUsd ?? 1;
  const taxUsdc6 = rawToUsdc6(taxRaw, ttDec, ttPrice);
  const amount = gross.amount - taxUsdc6;
  if (amount > MAX_SANE_NET_USDC6 || amount < -MAX_SANE_NET_USDC6) return null;
  return { ...gross, amount };
}

/**
 * Aggregate net weekly rewards across multiple pools for a user's portfolio.
 *
 * Skips pools with 'native' kind since you can't meaningfully sum different
 * token denominations into one USD figure. Returns total net in USDC 6-dec.
 */
export function aggregateNetRewards(
  pools: Array<{
    params: PoolRewardParams;
    seats: Array<{ price: bigint; taxRateBps: bigint; taxTokenDecimals?: number; taxTokenPriceUsd?: number }>;
  }>,
): bigint {
  let total = 0n;
  for (const { params, seats } of pools) {
    const gross = grossRewardsPerTile(params);
    if (gross.kind !== 'usd') continue;
    for (const seat of seats) {
      const net = netRewardsPerTile(gross, seat.price, seat.taxRateBps, {
        taxTokenDecimals: seat.taxTokenDecimals,
        taxTokenPriceUsd: seat.taxTokenPriceUsd,
      });
      // Skip rather than sum — an unreliable per-seat net (helper
      // returned `null`) would poison the whole portfolio total. Same
      // guarantee `aggregateNetRewards`'s `gross.kind !== 'usd'`
      // continue-skip provides for unconvertible reward tokens.
      if (!net) continue;
      total += net.amount;
    }
  }
  return total;
}

// ---------------------------------------------------------------------------
// Display formatting
// ---------------------------------------------------------------------------

/**
 * Format a RewardResult for UI display.
 *
 * 'usd' kind:    "+$2.45"  or "-$0.12"
 * 'native' kind: "12.23K DEGEN"
 */
export function formatRewardValue(result: RewardResult, opts?: { showSign?: boolean }): string {
  if (result.kind === 'usd') {
    return formatUSD(result.amount, { showSign: opts?.showSign });
  }
  const human = Number(result.amount) / 10 ** result.decimals;
  return formatTokenCompact(human, result.symbol);
}
